import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ExternalLink } from "lucide-react";
import { JOURNEY_EVENTS, RAIL_LABELS, RAIL_MONTHS, TRACKS, railIndex } from "@/lib/journey-events";

const COLOR_MAP = {
  blue:  { bg: "#dbeafe", border: "#93c5fd", text: "#1e40af" },
  amber: { bg: "#fef3c7", border: "#fcd34d", text: "#92400e" },
  red:   { bg: "#fee2e2", border: "#fca5a5", text: "#991b1b" },
  green: { bg: "#dcfce7", border: "#86efac", text: "#166534" },
};

/**
 * JourneyMap, horizontal calendar (Aug→Jun) with three swimlanes:
 *   Track 1: FAFSA & Documents (blue)
 *   Track 2: State Deadlines    (amber/red)
 *   Track 3: Awards & Decisions (green)
 *
 * Mobile: scrolls horizontally. Auto-scrolls today's month into view
 * on mount. Track labels move above each row on narrow screens.
 */
export default function JourneyMap() {
  const scrollerRef = useRef(null);
  const todayRef = useRef(null);
  const [hint, setHint] = useState(true);

  const today = useMemo(() => new Date(), []);
  const currentMonth = today.getMonth() + 1; // 1-12
  const todayIdx = railIndex(currentMonth);

  // Was the event before today on the calendar? Past months → opacity-50.
  const isPast = (month) => railIndex(month) < todayIdx;

  // Auto-scroll today into view inside the horizontal rail (mobile).
  // We target the nearest scrolling ancestor explicitly so the document
  // viewport is NOT pulled along with the inner scroll, which used to
  // open the homepage mid-page on first load.
  useEffect(() => {
    const el = todayRef.current;
    if (!el) return;
    // Walk up to find the horizontally scrollable container.
    let parent = el.parentElement;
    while (parent) {
      const cs = window.getComputedStyle(parent);
      const overflowX = cs.overflowX;
      if ((overflowX === "auto" || overflowX === "scroll") && parent.scrollWidth > parent.clientWidth) {
        const left = el.offsetLeft - parent.clientWidth / 2 + el.clientWidth / 2;
        parent.scrollTo({ left: Math.max(0, left), behavior: "smooth" });
        break;
      }
      parent = parent.parentElement;
    }
    const t = setTimeout(() => setHint(false), 2500);
    return () => clearTimeout(t);
  }, []);

  return (
    <div className="rounded-xl border" style={{ borderColor: "#e5e7eb", background: "#ffffff" }} data-testid="journey-map">
      <div className="px-5 sm:px-7 pt-6 pb-3 flex items-baseline justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-[0.2em]" style={{ color: "#9ca3af" }}>The map</div>
          <h2 className="font-heading text-2xl mt-1" style={{ color: "#111928" }}>Your year at a glance.</h2>
        </div>
        <div className="text-xs" style={{ color: "#6b7280" }} data-testid="journey-today-label">
          Today: {today.toLocaleDateString("en-US", { month: "long", day: "numeric" })}
        </div>
      </div>

      {/* Scroller */}
      <div className="relative">
        <div
          ref={scrollerRef}
          className="journey-map-scroll px-5 sm:px-7 pb-7"
          style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
          <div style={{ minWidth: "1480px", position: "relative" }}>
            {/* Today vertical marker */}
            <div
              ref={todayRef}
              id="journey-today-marker"
              data-testid="journey-today-marker"
              style={{
                position: "absolute",
                top: 0,
                bottom: 0,
                left: `calc(${(todayIdx / (RAIL_MONTHS.length - 1)) * 100}% )`,
                width: "1px",
                background: "#1d4ed8",
                opacity: 0.45,
                pointerEvents: "none",
                zIndex: 0,
              }}
              aria-hidden>
              <span
                className="absolute -translate-x-1/2 px-2 py-0.5 rounded-md text-[10px] uppercase tracking-[0.16em] font-medium"
                style={{ top: -8, left: 0, background: "#1d4ed8", color: "#ffffff" }}>
                Today
              </span>
            </div>

            {/* Month rail */}
            <div className="grid relative pt-6 pb-3 mb-2"
              style={{ gridTemplateColumns: `repeat(${RAIL_MONTHS.length}, minmax(120px, 1fr))`, borderBottom: "1px solid #e5e7eb" }}>
              {RAIL_LABELS.map((m, i) => (
                <div key={m + i} className="text-sm text-center font-medium"
                  style={{ color: i === todayIdx ? "#1d4ed8" : "#6b7280" }}>
                  {m}
                </div>
              ))}
            </div>

            {/* Tracks */}
            {Object.values(TRACKS).map((track) => (
              <Track
                key={track.id}
                track={track}
                events={JOURNEY_EVENTS.filter(e => e.track === track.id)}
                isPast={isPast}
              />
            ))}
          </div>
        </div>

        {hint && (
          <div className="md:hidden absolute right-3 top-3 text-[10px] px-2 py-1 rounded-md fade-in pointer-events-none"
            style={{ background: "rgba(17,25,40,0.85)", color: "#f9fafb" }}
            data-testid="journey-scroll-hint">
            ← scroll →
          </div>
        )}
      </div>
    </div>
  );
}

function Track({ track, events, isPast }) {
  return (
    <div className="mb-4">
      <div className="text-[11px] uppercase tracking-[0.18em] mb-2 font-medium"
        style={{ color: "#6b7280" }}
        data-testid={`journey-track-${track.id}`}>
        {track.label}
      </div>
      <div className="grid relative" style={{
        gridTemplateColumns: `repeat(${RAIL_MONTHS.length}, minmax(120px, 1fr))`,
        minHeight: 64,
        background: "rgba(0,0,0,0.018)",
        borderRadius: 10,
        padding: "12px 0",
        rowGap: 8,
      }}>
        {events.map(e => {
          const idx = railIndex(e.month);
          const past = isPast(e.month);
          const c = COLOR_MAP[e.color] || COLOR_MAP.blue;
          return (
            <EventPill
              key={e.id}
              event={e}
              col={idx + 1}
              past={past}
              colors={c}
            />
          );
        })}
      </div>
    </div>
  );
}

function EventPill({ event, col, past, colors }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  // close tooltip on outside click
  useEffect(() => {
    if (!open) return;
    const onDoc = (ev) => { if (ref.current && !ref.current.contains(ev.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  return (
    <div
      ref={ref}
      style={{ gridColumn: `${col} / span 1`, position: "relative", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        data-testid={`journey-event-${event.id}`}
        className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-transform hover:scale-[1.02] active:scale-[0.98] shadow-sm"
        style={{
          background: colors.bg,
          border: `1px solid ${colors.border}`,
          color: colors.text,
          opacity: past ? 0.55 : 1,
          letterSpacing: "0.005em",
          animation: event.urgent && !past ? "pulse-soft 2.6s ease-in-out infinite" : "none",
        }}>
        {event.label}
      </button>

      {open && (
        <div
          role="tooltip"
          className="absolute z-30 mt-2 p-3 rounded-lg shadow-lg fade-in"
          style={{
            top: "100%",
            left: "50%",
            transform: "translateX(-50%)",
            background: "#111928",
            color: "#f9fafb",
            width: 240,
            fontSize: 12,
            lineHeight: 1.5,
          }}
          data-testid={`journey-tip-${event.id}`}>
          <div className="font-medium mb-1">{event.label}</div>
          <div style={{ color: "#9ca3af" }} className="mb-2">{event.sublabel}</div>
          <div className="mb-3">{event.tip}</div>
          <div className="flex gap-2 flex-wrap">
            {event.tool && (
              <Link to={event.tool} className="text-[11px] underline" style={{ color: "#93c5fd" }}>
                Open tool →
              </Link>
            )}
            {event.externalLink && (
              <a href={event.externalLink} target="_blank" rel="noopener noreferrer"
                className="text-[11px] underline inline-flex items-center gap-1" style={{ color: "#93c5fd" }}>
                {event.externalLabel || "Open ↗"} <ExternalLink className="w-3 h-3" />
              </a>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
